import { useParams, Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categories } from '@/mocks/movies';

const categoryMeta: Record<string, { title: string; desc: string; keywords: string }> = {
  action: {
    title: '动作片在线观看 - 天天影院',
    desc: '天天影院动作片频道提供最新最全的动作片在线观看，高清画质极速播放不卡顿。汇集国内外经典动作片、枪战片、警匪片、武侠片，免费在线看。',
    keywords: '动作片在线,动作电影,枪战片,警匪片,武侠片,动作大片,天天影院',
  },
  comedy: {
    title: '喜剧片免费看 - 天天影院',
    desc: '天天影院喜剧片频道提供爆笑喜剧片免费在线观看。最新最全的搞笑电影、喜剧大片、幽默短片，极速高清播放，让你笑不停。',
    keywords: '喜剧片免费看,搞笑电影,喜剧电影,幽默电影,喜剧大片,天天影院',
  },
  scifi: {
    title: '科幻片高清播放 - 天天影院',
    desc: '天天影院科幻片频道提供最新科幻片高清播放，汇集太空探索、人工智能、时间旅行等题材科幻电影，震撼视觉体验，免费在线观看。',
    keywords: '科幻片高清播放,科幻电影,太空电影,科幻大片,未来电影,天天影院',
  },
  classic: {
    title: '经典电影全集 - 天天影院',
    desc: '天天影院经典电影频道汇集中外影史经典电影全集，包括豆瓣高分电影、奥斯卡获奖影片、永恒银幕经典，高清修复免费在线观看。',
    keywords: '经典电影全集,经典电影,老电影,豆瓣高分,奥斯卡电影,天天影院',
  },
  romance: {
    title: '爱情片 - 天天影院',
    desc: '天天影院爱情片频道提供最新最感人的爱情电影在线观看。浪漫爱情片、青春爱情电影，高清画质免费看。',
    keywords: '爱情片,爱情电影,浪漫电影,青春爱情片,恋爱电影,天天影院',
  },
  horror: {
    title: '恐怖片 - 天天影院',
    desc: '天天影院恐怖片频道提供最新恐怖电影在线观看。惊悚恐怖片、悬疑电影、灵异电影，高清画质极速播放，免费在线看。',
    keywords: '恐怖片,恐怖电影,惊悚片,悬疑电影,灵异电影,天天影院',
  },
  animation: {
    title: '动画片 - 天天影院',
    desc: '天天影院动画片频道提供最新动画电影在线观看。国产动画、日本动漫、好莱坞动画大片，高清画质免费看。',
    keywords: '动画片,动画电影,动漫电影,卡通电影,动画大片,天天影院',
  },
  documentary: {
    title: '纪录片 - 天天影院',
    desc: '天天影院纪录片频道提供最新纪录片在线观看。自然纪录片、历史纪录片、人文纪录片，高清画质免费在线看。',
    keywords: '纪录片,纪录片电影,自然纪录片,历史纪录片,人文纪录片,天天影院',
  },
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const meta = slug ? categoryMeta[slug] : null;
  const categoryMovies = movies.filter((m) => m.categorySlug === slug);

  if (!category || !meta) {
    return (
      <div className="pt-24 text-center py-20">
        <h2 className="text-xl text-foreground-200">分类未找到</h2>
        <Link to="/" className="text-primary-500 hover:text-primary-400 mt-4 inline-block">返回首页</Link>
      </div>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://tiantiancinema.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category.name,
        item: `https://tiantiancinema.com/category/${slug}`,
      },
    ],
  };

  return (
    <>
      <PageSEO
        title={meta.title}
        description={meta.desc}
        keywords={meta.keywords}
        canonicalUrl={`https://tiantiancinema.com/category/${slug}`}
        ogTitle={meta.title}
        ogDescription={meta.desc}
        ogUrl={`https://tiantiancinema.com/category/${slug}`}
        structuredData={[breadcrumbSchema]}
      />

      <Breadcrumb />

      <main className="w-full max-w-[1400px] mx-auto px-4 md:px-6 pb-16">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-2">
            {category.name}
          </h1>
          <p className="text-sm text-foreground-500 max-w-2xl">
            {meta.desc}
          </p>
        </div>

        {categoryMovies.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {categoryMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-film-line text-2xl text-foreground-600"></i>
            </div>
            <p className="text-foreground-500">暂无{category.name}资源</p>
            <Link to="/" className="text-primary-500 hover:text-primary-400 mt-3 inline-block text-sm">
              返回首页查看更多
            </Link>
          </div>
        )}
      </main>
    </>
  );
}